// src/components/admin/AdminDashboard.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./AdminDashboard.css";

const AdminDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [activeSubTab, setActiveSubTab] = useState("messages");
  const [stats, setStats] = useState({
    totalLeads: 0,
    newLeads: 0,
    activeProjects: 0,
    completedProjects: 0,
    monthlyRevenue: 0,
    pendingInvoices: 0,
    conversionRate: 0,
    totalClients: 0,
  });
  const [leads, setLeads] = useState([]);
  const [projects, setProjects] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [threatLogs, setThreatLogs] = useState([]);
  const [blockedIPs, setBlockedIPs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showQuickAction, setShowQuickAction] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [creatingQuote, setCreatingQuote] = useState(false);
  const [quoteForm, setQuoteForm] = useState({
    client: '', title: '', description: '', amount: '', depositOnly: false, source: 'call', adminNotes: ''
  });

  useEffect(() => {
    fetchDashboardData();
    fetchChatData();
    // Refresh data every 30 seconds
    const interval = setInterval(() => {
      fetchDashboardData();
      fetchChatData();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetchQuotes();
    fetchClients();
  }, []);

  const fetchDashboardData = async () => {
    try {
        const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      // Fetch all data in parallel
      const [statsRes, leadsRes, projectsRes, invoicesRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/api/admin/stats`, { headers }),
        fetch(`${process.env.REACT_APP_API_URL}/api/leads`, { headers }),
        fetch(`${process.env.REACT_APP_API_URL}/api/projects`, { headers }),
        fetch(`${process.env.REACT_APP_API_URL}/api/invoices`, { headers }),
      ]);

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }

      if (leadsRes.ok) {
        const leadsData = await leadsRes.json();
        setLeads(leadsData);
      }

      if (projectsRes.ok) {
        const projectsData = await projectsRes.json();
        setProjects(projectsData);
      }

      if (invoicesRes.ok) {
        const invoicesData = await invoicesRes.json();
        setInvoices(invoicesData);
      }

      setLoading(false);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      setLoading(false);
    }
  };

  const fetchChatData = async () => {
    try {
      const token =
        localStorage.getItem("token") || localStorage.getItem("authToken");
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      const [messagesRes, threatsRes, blockedRes] = await Promise.all([
        fetch(
          `${
            process.env.REACT_APP_API_URL || "http://localhost:5001"
          }/api/admin/chat-messages`,
          { headers }
        ),
        fetch(
          `${
            process.env.REACT_APP_API_URL || "http://localhost:5001"
          }/api/admin/threat-logs`,
          { headers }
        ),
        fetch(
          `${
            process.env.REACT_APP_API_URL || "http://localhost:5001"
          }/api/admin/blocked-ips`,
          { headers }
        ),
      ]);

      if (messagesRes.ok) {
        const messages = await messagesRes.json();
        setChatMessages(messages);
      }

      if (threatsRes.ok) {
        const threats = await threatsRes.json();
        setThreatLogs(threats);
      }

      if (blockedRes.ok) {
        const blocked = await blockedRes.json();
        setBlockedIPs(blocked);
      }
    } catch (error) {
      console.error("Error fetching chat data:", error);
    }
  };

  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      const token =
        localStorage.getItem("token") || localStorage.getItem("authToken");
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/leads/${leadId}/status`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ status: newStatus }),
        }
      );

      if (response.ok) {
        fetchDashboardData();
        alert("Lead status updated successfully!");
      }
    } catch (error) {
      console.error("Error updating lead:", error);
    }
  };

  const convertLeadToProject = async (leadId) => {
    try {
      const token =
        localStorage.getItem("token") || localStorage.getItem("authToken");
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/leads/${leadId}/convert`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const project = await response.json();
        alert("Lead converted to project successfully!");
        navigate(`/admin/projects/${project._id}`);
      }
    } catch (error) {
      console.error("Error converting lead:", error);
    }
  };

  const sendInvoiceReminder = async (invoiceId) => {
    try {
      const token =
        localStorage.getItem("token") || localStorage.getItem("authToken");
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/invoices/${invoiceId}/remind`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        alert("Reminder sent successfully!");
      }
    } catch (error) {
      console.error("Error sending reminder:", error);
    }
  };

  const viewFullMessage = (message) => {
    alert(
      `Full Message:\n\n${message.message}\n\nFrom: ${
        message.name || "Anonymous"
      }\nEmail: ${message.email || "N/A"}\nIP: ${message.ip}`
    );
  };

  const replyToMessage = (message) => {
    if (message.email) {
      window.location.href = `mailto:${message.email}?subject=Re: Your message on SpinoSoftBits`;
    }
  };

  const flagMessage = async (messageId) => {
    try {
      const token =
        localStorage.getItem("token") || localStorage.getItem("authToken");
      await fetch(
        `${
          process.env.REACT_APP_API_URL || "http://localhost:5001"
        }/api/admin/flag-message/${messageId}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      fetchChatData();
    } catch (error) {
      console.error("Error flagging message:", error);
    }
  };

  const blockIP = async (ip) => {
    if (window.confirm(`Are you sure you want to block IP: ${ip}?`)) {
      try {
        const token =
          localStorage.getItem("token") || localStorage.getItem("authToken");
        await fetch(
          `${
            process.env.REACT_APP_API_URL || "http://localhost:5001"
          }/api/admin/block-ip`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              ip,
              reason: "Manual block from admin",
              permanent: false,
            }),
          }
        );
        fetchChatData();
        alert("IP blocked successfully");
      } catch (error) {
        console.error("Error blocking IP:", error);
      }
    }
  };

  const unblockIP = async (blockedId) => {
    if (window.confirm("Are you sure you want to unblock this IP?")) {
      try {
        const token =
          localStorage.getItem("token") || localStorage.getItem("authToken");
        await fetch(
          `${
            process.env.REACT_APP_API_URL || "http://localhost:5001"
          }/api/admin/unblock-ip/${blockedId}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        fetchChatData();
        alert("IP unblocked successfully");
      } catch (error) {
        console.error("Error unblocking IP:", error);
      }
    }
  };

  const reportToAuthorities = async (threat) => {
    alert(
      `Threat report prepared:\n\nIP: ${threat.ip}\nLocation: ${threat.location?.city}, ${threat.location?.country}\nMessage: ${threat.message}\n\nThis would be sent to appropriate authorities.`
    );
  };

  const fetchQuotes = async () => {
    try {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/quotes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setQuotes(data.quotes || []);
      }
    } catch (e) {
      console.error('Failed to load quotes', e);
    }
  };

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/quotes/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setClients(data.clients || []);
      }
    } catch (e) {
      console.error('Failed to load clients', e);
    }
  };

  const handleCreateQuote = async () => {
    if (!quoteForm.client || !quoteForm.title || !quoteForm.amount) {
      alert('Client, title and amount are required.');
      return;
    }
    try {
      setCreatingQuote(true);
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/quotes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          client: quoteForm.client,
          title: quoteForm.title,
          description: quoteForm.description,
          amount: Number(quoteForm.amount),
          depositOnly: quoteForm.depositOnly,
          source: quoteForm.source,
          adminNotes: quoteForm.adminNotes
        })
      });
      const data = await res.json();
      if (res.ok) {
        setQuoteForm({ client: '', title: '', description: '', amount: '', depositOnly: false, source: 'call', adminNotes: '' });
        setShowQuoteForm(false);
        fetchQuotes();
      } else {
        alert(data.error || 'Failed to create quote.');
      }
    } catch (e) {
      alert('Could not reach the server.');
    } finally {
      setCreatingQuote(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      new: "#3498db",
      contacted: "#f39c12",
      qualified: "#9b59b6",
      converted: "#27ae60",
      lost: "#e74c3c",
      pending: "#f39c12",
      in_progress: "#3498db",
      completed: "#27ae60",
      paid: "#27ae60",
      overdue: "#e74c3c",
      sent: "#f39c12",
    };
    return colors[status] || "#95a5a6";
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Loading dashboard data...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <div className="header-left">
          <h1>Admin Dashboard</h1>
          <button onClick={() => navigate("/")} className="home-btn">
            🏠 Back to Site
          </button>
        </div>
        <div className="admin-user-info">
          <span>Welcome, Spinoza</span>
          <button
            onClick={() => setActiveTab("settings")}
            className="settings-btn"
          >
            ⚙️ Settings
          </button>
          <button
            onClick={() => {
              if (onLogout) {
                onLogout();
              } else {
                localStorage.removeItem("token");
                localStorage.removeItem("authToken");
                localStorage.removeItem("userInfo");
                sessionStorage.removeItem("authToken");
              }
              navigate("/");
            }}
            className="logout-btn"
          >
            🚪 Logout
          </button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon leads-icon">📧</div>
          <div className="stat-content">
            <h3>Total Leads</h3>
            <p className="stat-number">{stats.totalLeads}</p>
            <span className="stat-badge new">{stats.newLeads} new</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon projects-icon">📊</div>
          <div className="stat-content">
            <h3>Active Projects</h3>
            <p className="stat-number">{stats.activeProjects}</p>
            <span className="stat-detail">
              {stats.completedProjects} completed
            </span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon revenue-icon">💰</div>
          <div className="stat-content">
            <h3>Monthly Revenue</h3>
            <p className="stat-number">
              {formatCurrency(stats.monthlyRevenue)}
            </p>
            <span className="stat-detail">{stats.pendingInvoices} pending</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon conversion-icon">📈</div>
          <div className="stat-content">
            <h3>Conversion Rate</h3>
            <p className="stat-number">{stats.conversionRate}%</p>
            <span className="stat-detail">{stats.totalClients} clients</span>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="admin-tabs">
        <button
          className={`tab-btn ${activeTab === "overview" ? "active" : ""}`}
          onClick={() => setActiveTab("overview")}
        >
          Overview
        </button>
        <button
          className={`tab-btn ${activeTab === "leads" ? "active" : ""}`}
          onClick={() => setActiveTab("leads")}
        >
          Leads ({leads.filter((l) => l.status === "new").length})
        </button>
        <button
          className={`tab-btn ${activeTab === "projects" ? "active" : ""}`}
          onClick={() => setActiveTab("projects")}
        >
          Projects
        </button>
        <button
          className={`tab-btn ${activeTab === "invoices" ? "active" : ""}`}
          onClick={() => setActiveTab("invoices")}
        >
          Invoices
        </button>
        <button
          className={`tab-btn ${activeTab === "quotes" ? "active" : ""}`}
          onClick={() => setActiveTab("quotes")}
        >
          Quotes ({quotes.filter((q) => q.status === "sent").length})
        </button>
        <button
          className={`tab-btn ${activeTab === "analytics" ? "active" : ""}`}
          onClick={() => setActiveTab("analytics")}
        >
          Analytics
        </button>
        <button
          className={`tab-btn ${activeTab === "chat" ? "active" : ""}`}
          onClick={() => setActiveTab("chat")}
        >
          Chat & Security (
          {threatLogs.filter((t) => t.severity === "critical").length})
        </button>
        <button
          className={`tab-btn ${activeTab === "settings" ? "active" : ""}`}
          onClick={() => setActiveTab("settings")}
        >
          Settings
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === "overview" && (
          <div className="overview-content">
            {/* Recent Activity */}
            <div className="recent-activity">
              <h2>Recent Activity</h2>
              <div className="activity-list">
                {leads.slice(0, 5).map((lead) => (
                  <div key={lead._id} className="activity-item">
                    <div className="activity-icon">🆕</div>
                    <div className="activity-details">
                      <p>
                        <strong>{lead.name}</strong> submitted a quote request
                      </p>
                      <span className="activity-time">
                        {formatDate(lead.createdAt)}
                      </span>
                    </div>
                    <button
                      className="quick-action-btn"
                      onClick={() => updateLeadStatus(lead._id, "contacted")}
                    >
                      Mark Contacted
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="quick-actions">
              <h2>Quick Actions</h2>
              <div className="action-buttons">
                <button
                  onClick={() => navigate("/admin/projects/new")}
                  className="action-btn primary"
                >
                  ➕ New Project
                </button>
                <button
                  onClick={() => navigate("/admin/invoices/new")}
                  className="action-btn success"
                >
                  📄 Create Invoice
                </button>
                <button
                  onClick={() => setShowQuickAction(true)}
                  className="action-btn info"
                >
                  📧 Send Update
                </button>
                <button
                  onClick={() => navigate("/admin/reports")}
                  className="action-btn warning"
                >
                  📊 Generate Report
                </button>
              </div>
            </div>
          </div>
        )}
        {activeTab === "settings" && (
          <div className="settings-content">
            <h2>Admin Settings</h2>

            <div className="settings-grid">
              {/* Print/Export Section */}
              <div className="settings-card">
                <h3>📄 Reports & Printing</h3>
                <div className="settings-options">
                  <button
                    className="settings-action-btn"
                    onClick={() => {
                      const printContent = document.createElement("div");
                      printContent.innerHTML = `
                        <h1>Client List - SpinoSoftBits</h1>
                        <p>Generated: ${new Date().toLocaleDateString()}</p>
                        <table style="width: 100%; border-collapse: collapse;">
                          <thead>
                            <tr style="background: #f0f0f0;">
                              <th style="border: 1px solid #ddd; padding: 8px;">Name</th>
                              <th style="border: 1px solid #ddd; padding: 8px;">Email</th>
                              <th style="border: 1px solid #ddd; padding: 8px;">Projects</th>
                              <th style="border: 1px solid #ddd; padding: 8px;">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            ${projects
                              .map(
                                (p) => `
                              <tr>
                                <td style="border: 1px solid #ddd; padding: 8px;">${p.client?.profile?.firstName} ${p.client?.profile?.lastName}</td>
                                <td style="border: 1px solid #ddd; padding: 8px;">${p.client?.email}</td>
                                <td style="border: 1px solid #ddd; padding: 8px;">${p.title}</td>
                                <td style="border: 1px solid #ddd; padding: 8px;">${p.status}</td>
                              </tr>
                            `
                              )
                              .join("")}
                          </tbody>
                        </table>
                      `;
                      const printWindow = window.open("", "_blank");
                      printWindow.document.write(printContent.innerHTML);
                      printWindow.document.close();
                      printWindow.print();
                    }}
                  >
                    🖨️ Print Client List
                  </button>

                  <button
                    className="settings-action-btn"
                    onClick={() => {
                      const printContent = document.createElement("div");
                      printContent.innerHTML = `
                        <h1>Lead Report - SpinoSoftBits</h1>
                        <p>Generated: ${new Date().toLocaleDateString()}</p>
                        <table style="width: 100%; border-collapse: collapse;">
                          <thead>
                            <tr style="background: #f0f0f0;">
                              <th style="border: 1px solid #ddd; padding: 8px;">Date</th>
                              <th style="border: 1px solid #ddd; padding: 8px;">Name</th>
                              <th style="border: 1px solid #ddd; padding: 8px;">Email</th>
                              <th style="border: 1px solid #ddd; padding: 8px;">Service</th>
                              <th style="border: 1px solid #ddd; padding: 8px;">Budget</th>
                              <th style="border: 1px solid #ddd; padding: 8px;">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            ${leads
                              .map(
                                (l) => `
                              <tr>
                                <td style="border: 1px solid #ddd; padding: 8px;">${formatDate(
                                  l.createdAt
                                )}</td>
                                <td style="border: 1px solid #ddd; padding: 8px;">${
                                  l.name
                                }</td>
                                <td style="border: 1px solid #ddd; padding: 8px;">${
                                  l.email
                                }</td>
                                <td style="border: 1px solid #ddd; padding: 8px;">${
                                  l.service
                                }</td>
                                <td style="border: 1px solid #ddd; padding: 8px;">${formatCurrency(
                                  l.budget
                                )}</td>
                                <td style="border: 1px solid #ddd; padding: 8px;">${
                                  l.status
                                }</td>
                              </tr>
                            `
                              )
                              .join("")}
                          </tbody>
                        </table>
                      `;
                      const printWindow = window.open("", "_blank");
                      printWindow.document.write(printContent.innerHTML);
                      printWindow.document.close();
                      printWindow.print();
                    }}
                  >
                    📊 Print Lead Report
                  </button>

                  <button
                    className="settings-action-btn"
                    onClick={() => {
                      const csvContent =
                        "data:text/csv;charset=utf-8," +
                        "Name,Email,Phone,Service,Budget,Status\n" +
                        leads
                          .map(
                            (l) =>
                              `${l.name},${l.email},${l.phone},${l.service},${l.budget},${l.status}`
                          )
                          .join("\n");
                      const encodedUri = encodeURI(csvContent);
                      const link = document.createElement("a");
                      link.setAttribute("href", encodedUri);
                      link.setAttribute(
                        "download",
                        `leads_${new Date().toISOString().split("T")[0]}.csv`
                      );
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }}
                  >
                    💾 Export Leads to CSV
                  </button>

                  <button
                    className="settings-action-btn"
                    onClick={() => {
                      const csvContent =
                        "data:text/csv;charset=utf-8," +
                        "Client,Email,Project,Budget,Status,Progress\n" +
                        projects
                          .map(
                            (p) =>
                              `${p.client?.profile?.firstName} ${p.client?.profile?.lastName},${p.client?.email},${p.title},${p.budget},${p.status},${p.progress}%`
                          )
                          .join("\n");
                      const encodedUri = encodeURI(csvContent);
                      const link = document.createElement("a");
                      link.setAttribute("href", encodedUri);
                      link.setAttribute(
                        "download",
                        `projects_${new Date().toISOString().split("T")[0]}.csv`
                      );
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }}
                  >
                    💾 Export Projects to CSV
                  </button>
                </div>
              </div>

              {/* Quick Stats Summary */}
              <div className="settings-card">
                <h3>📈 Business Summary</h3>
                <div className="summary-stats">
                  <div className="summary-item">
                    <span>Total Revenue (This Month):</span>
                    <strong>{formatCurrency(stats.monthlyRevenue)}</strong>
                  </div>
                  <div className="summary-item">
                    <span>Active Projects:</span>
                    <strong>{stats.activeProjects}</strong>
                  </div>
                  <div className="summary-item">
                    <span>Total Clients:</span>
                    <strong>{stats.totalClients}</strong>
                  </div>
                  <div className="summary-item">
                    <span>Conversion Rate:</span>
                    <strong>{stats.conversionRate}%</strong>
                  </div>
                  <button
                    className="settings-action-btn"
                    onClick={() => {
                      window.print();
                    }}
                  >
                    🖨️ Print Dashboard Summary
                  </button>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="settings-card">
                <h3>⚡ Quick Actions</h3>
                <div className="settings-options">
                  <button
                    className="settings-action-btn"
                    onClick={() => {
                      if (
                        window.confirm(
                          "This will clear all browser cache. Continue?"
                        )
                      ) {
                        localStorage.clear();
                        sessionStorage.clear();
                        alert("Cache cleared! Please login again.");
                        navigate("/login");
                      }
                    }}
                  >
                    🗑️ Clear Cache
                  </button>

                  <button
                    className="settings-action-btn"
                    onClick={() => {
                      fetchDashboardData();
                      fetchChatData();
                      alert("Data refreshed successfully!");
                    }}
                  >
                    🔄 Refresh All Data
                  </button>

                  <button
                    className="settings-action-btn"
                    onClick={() => navigate("/admin/backup")}
                  >
                    💾 Backup Database
                  </button>
                </div>
              </div>

              {/* Business Info */}
              <div className="settings-card">
                <h3>🏢 Business Information</h3>
                <div className="business-info">
                  <p>
                    <strong>Company:</strong> SpinoSoftBits
                  </p>
                  <p>
                    <strong>Admin:</strong> Spinoza Delva
                  </p>
                  <p>
                    <strong>Email:</strong> spinoza@spinozasoftbits.com
                  </p>
                  <p>
                    <strong>Phone:</strong> (917) 808-7320
                  </p>
                  <p>
                    <strong>Dashboard Version:</strong> 2.0.1
                  </p>
                  <p>
                    <strong>Last Login:</strong> {new Date().toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "leads" && (
          <div className="leads-content">
            <div className="content-header">
              <h2>Lead Management</h2>
              <div className="lead-filters">
                <select
                  onChange={(e) => console.log("Filter:", e.target.value)}
                >
                  <option value="all">All Leads</option>
                  <option value="new">New</option>
                  <option value="contacted">Contacted</option>
                  <option value="qualified">Qualified</option>
                  <option value="converted">Converted</option>
                </select>
              </div>
            </div>

            <div className="leads-table">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Service</th>
                    <th>Budget</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead) => (
                    <tr key={lead._id} onClick={() => setSelectedLead(lead)}>
                      <td>{formatDate(lead.createdAt)}</td>
                      <td>{lead.name}</td>
                      <td>{lead.email}</td>
                      <td>{lead.service}</td>
                      <td>{formatCurrency(lead.budget)}</td>
                      <td>
                        <span
                          className="status-badge"
                          style={{
                            backgroundColor: getStatusColor(lead.status),
                          }}
                        >
                          {lead.status}
                        </span>
                      </td>
                      <td>
                        <div className="action-dropdown">
                          <button className="action-toggle">⋮</button>
                          <div className="dropdown-menu">
                            <button
                              onClick={() =>
                                updateLeadStatus(lead._id, "contacted")
                              }
                            >
                              Mark Contacted
                            </button>
                            <button
                              onClick={() =>
                                updateLeadStatus(lead._id, "qualified")
                              }
                            >
                              Mark Qualified
                            </button>
                            <button
                              onClick={() => convertLeadToProject(lead._id)}
                            >
                              Convert to Project
                            </button>
                            <button
                              onClick={() =>
                                navigate(`/admin/leads/${lead._id}`)
                              }
                            >
                              View Details
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === "projects" && (
          <div className="projects-content">
            <div className="content-header">
              <h2>Project Management</h2>
              <button
                onClick={() => navigate("/admin/projects/new")}
                className="add-btn"
              >
                + New Project
              </button>
            </div>

            <div className="projects-grid">
              {projects.map((project) => (
                <div key={project._id} className="project-card">
                  <div className="project-header">
                    <h3>{project.title}</h3>
                    <span
                      className="project-status"
                      style={{
                        backgroundColor: getStatusColor(project.status),
                      }}
                    >
                      {project.status}
                    </span>
                  </div>
                  <div className="project-details">
                    <p>
                      <strong>Client:</strong>{" "}
                      {project.client?.profile?.firstName}{" "}
                      {project.client?.profile?.lastName}
                    </p>
                    <p>
                      <strong>Budget:</strong> {formatCurrency(project.budget)}
                    </p>
                    <p>
                      <strong>Deadline:</strong> {formatDate(project.deadline)}
                    </p>
                    <div className="project-progress">
                      <div className="progress-bar">
                        <div
                          className="progress-fill"
                          style={{ width: `${project.progress || 0}%` }}
                        ></div>
                      </div>
                      <span>{project.progress || 0}%</span>
                    </div>
                  </div>
                  <div className="project-actions">
                    <button
                      onClick={() => navigate(`/admin/projects/${project._id}`)}
                    >
                      View Details
                    </button>
                    <button
                      onClick={() =>
                        navigate(`/admin/projects/${project._id}/update`)
                      }
                    >
                      Send Update
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === "invoices" && (
          <div className="invoices-content">
            <div className="content-header">
              <h2>Invoice Management</h2>
              <button
                onClick={() => navigate("/admin/invoices/new")}
                className="add-btn"
              >
                + Create Invoice
              </button>
            </div>

            <div className="invoices-table">
              <table>
                <thead>
                  <tr>
                    <th>Invoice #</th>
                    <th>Client</th>
                    <th>Amount</th>
                    <th>Due Date</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice) => (
                    <tr key={invoice._id}>
                      <td>{invoice.invoiceNumber}</td>
                      <td>
                        {invoice.client?.profile?.firstName}{" "}
                        {invoice.client?.profile?.lastName}
                      </td>
                      <td>{formatCurrency(invoice.total)}</td>
                      <td>{formatDate(invoice.dueDate)}</td>
                      <td>
                        <span
                          className="status-badge"
                          style={{
                            backgroundColor: getStatusColor(invoice.status),
                          }}
                        >
                          {invoice.status}
                        </span>
                      </td>
                      <td>
                        <button
                          onClick={() =>
                            navigate(`/admin/invoices/${invoice._id}`)
                          }
                        >
                          View
                        </button>
                        {invoice.status === "sent" && (
                          <button
                            onClick={() => sendInvoiceReminder(invoice._id)}
                          >
                            Send Reminder
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === "quotes" && (
          <div className="quotes-content">
            <div className="content-header">
              <h2>Quotes</h2>
              <button
                onClick={() => setShowQuoteForm((v) => !v)}
                className="add-btn"
              >
                {showQuoteForm ? "Close" : "+ New Quote"}
              </button>
            </div>

            {showQuoteForm && (
              <div className="quote-form-card">
                <div className="quote-form-grid">
                  <label>
                    Client
                    <select
                      value={quoteForm.client}
                      onChange={(e) => setQuoteForm({ ...quoteForm, client: e.target.value })}
                    >
                      <option value="">Select a client…</option>
                      {clients.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name} — {c.email}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Title
                    <input
                      type="text"
                      value={quoteForm.title}
                      onChange={(e) => setQuoteForm({ ...quoteForm, title: e.target.value })}
                      placeholder="e.g. Restaurant web platform"
                    />
                  </label>
                  <label>
                    Amount (USD)
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={quoteForm.amount}
                      onChange={(e) => setQuoteForm({ ...quoteForm, amount: e.target.value })}
                      placeholder="3500"
                    />
                  </label>
                  <label>
                    Source
                    <select
                      value={quoteForm.source}
                      onChange={(e) => setQuoteForm({ ...quoteForm, source: e.target.value })}
                    >
                      <option value="call">Call</option>
                      <option value="zoom">Zoom</option>
                      <option value="in_person">In person</option>
                      <option value="email">Email</option>
                      <option value="other">Other</option>
                    </select>
                  </label>
                </div>

                <label className="quote-form-full">
                  Description (the client sees this)
                  <textarea
                    rows={3}
                    value={quoteForm.description}
                    onChange={(e) => setQuoteForm({ ...quoteForm, description: e.target.value })}
                    placeholder="Short scope summary the client reads on their Quotes tab"
                  />
                </label>

                <label className="quote-form-full">
                  Private notes (admin only — never shown to the client)
                  <textarea
                    rows={2}
                    value={quoteForm.adminNotes}
                    onChange={(e) => setQuoteForm({ ...quoteForm, adminNotes: e.target.value })}
                  />
                </label>

                <label className="quote-form-check">
                  <input
                    type="checkbox"
                    checked={quoteForm.depositOnly}
                    onChange={(e) => setQuoteForm({ ...quoteForm, depositOnly: e.target.checked })}
                  />
                  Charge 50% deposit only (client pays half now)
                </label>

                <div className="quote-form-actions">
                  <button
                    className="add-btn"
                    onClick={handleCreateQuote}
                    disabled={creatingQuote}
                  >
                    {creatingQuote ? "Creating…" : "Create & send"}
                  </button>
                </div>
              </div>
            )}

            <div className="quotes-table">
              <table>
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Client</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {quotes.map((q) => (
                    <tr key={q._id}>
                      <td>{q.title}</td>
                      <td>
                        {q.client?.profile?.firstName} {q.client?.profile?.lastName}
                        <br />
                        <small>{q.client?.email}</small>
                      </td>
                      <td>
                        {formatCurrency((q.amountCents || 0) / 100)}
                        {q.depositOnly && <small> (50% dep)</small>}
                      </td>
                      <td>
                        <span
                          className="status-badge"
                          style={{ backgroundColor: getStatusColor(q.status) }}
                        >
                          {q.status}
                        </span>
                      </td>
                      <td>{formatDate(q.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {quotes.length === 0 && (
                <p style={{ padding: "1rem", opacity: 0.7 }}>
                  No quotes yet. Create one above after a consult.
                </p>
              )}
            </div>
          </div>
        )}

        {activeTab === "analytics" && (
          <div className="analytics-content">
            <h2>Analytics & Reports</h2>

            <div className="analytics-grid">
              <div className="chart-card">
                <h3>Revenue Trend</h3>
                <div className="chart-placeholder">
                  <p>Revenue chart will go here</p>
                </div>
              </div>

              <div className="chart-card">
                <h3>Lead Sources</h3>
                <div className="chart-placeholder">
                  <p>Source breakdown chart</p>
                </div>
              </div>

              <div className="metrics-card">
                <h3>Key Metrics</h3>
                <ul>
                  <li>Average Project Value: {formatCurrency(5000)}</li>
                  <li>Client Lifetime Value: {formatCurrency(15000)}</li>
                  <li>Average Close Time: 7 days</li>
                  <li>Customer Satisfaction: 4.8/5</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {activeTab === "chat" && (
          <div className="chat-security-content">
            <div className="content-header">
              <h2>Chat Messages & Security Monitoring</h2>
              <div className="security-stats">
                <span className="security-stat">
                  <i className="uil uil-message"></i> {chatMessages.length}{" "}
                  Messages
                </span>
                <span className="security-stat threat">
                  <i className="uil uil-exclamation-triangle"></i>{" "}
                  {threatLogs.length} Threats
                </span>
                <span className="security-stat blocked">
                  <i className="uil uil-ban"></i> {blockedIPs.length} Blocked
                  IPs
                </span>
              </div>
            </div>

            {/* Threat Alerts Section */}
            {threatLogs.filter((t) => t.severity === "critical").length > 0 && (
              <div className="threat-alert-banner">
                <i className="uil uil-exclamation-circle"></i>
                <span>Critical threats detected! Review immediately.</span>
              </div>
            )}

            {/* Sub-tabs for Chat Security */}
            <div className="chat-subtabs">
              <button
                className={activeSubTab === "messages" ? "active" : ""}
                onClick={() => setActiveSubTab("messages")}
              >
                Chat Messages
              </button>
              <button
                className={activeSubTab === "threats" ? "active" : ""}
                onClick={() => setActiveSubTab("threats")}
              >
                Threat Logs
              </button>
              <button
                className={activeSubTab === "blocked" ? "active" : ""}
                onClick={() => setActiveSubTab("blocked")}
              >
                Blocked Users
              </button>
            </div>

            {/* Chat Messages */}
            {activeSubTab === "messages" && (
              <div className="chat-messages-table">
                <table>
                  <thead>
                    <tr>
                      <th>Date/Time</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Message</th>
                      <th>IP Address</th>
                      <th>Location</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {chatMessages.map((msg) => (
                      <tr
                        key={msg._id}
                        className={msg.flagged ? "flagged-row" : ""}
                      >
                        <td>{new Date(msg.timestamp).toLocaleString()}</td>
                        <td>{msg.name || "Anonymous"}</td>
                        <td>{msg.email || "-"}</td>
                        <td className="message-cell">
                          <div className="message-preview">
                            {msg.message.substring(0, 100)}
                            {msg.message.length > 100 && "..."}
                          </div>
                        </td>
                        <td className="ip-cell">{msg.ip}</td>
                        <td>
                          {msg.location?.city}, {msg.location?.country}
                        </td>
                        <td>
                          <span
                            className={`status-badge ${
                              msg.replied ? "replied" : "pending"
                            }`}
                          >
                            {msg.replied ? "Replied" : "Pending"}
                          </span>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="view-btn"
                              onClick={() => viewFullMessage(msg)}
                            >
                              View
                            </button>
                            <button
                              className="reply-btn"
                              onClick={() => replyToMessage(msg)}
                            >
                              Reply
                            </button>
                            {!msg.flagged && (
                              <button
                                className="flag-btn"
                                onClick={() => flagMessage(msg._id)}
                              >
                                Flag
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Threat Logs */}
            {activeSubTab === "threats" && (
              <div className="threat-logs-table">
                <table>
                  <thead>
                    <tr>
                      <th>Date/Time</th>
                      <th>Severity</th>
                      <th>Type</th>
                      <th>Message</th>
                      <th>Matched Words</th>
                      <th>IP Address</th>
                      <th>Location</th>
                      <th>Device</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {threatLogs.map((threat) => (
                      <tr
                        key={threat._id}
                        className={`threat-row ${threat.severity}`}
                      >
                        <td>{new Date(threat.timestamp).toLocaleString()}</td>
                        <td>
                          <span className={`severity-badge ${threat.severity}`}>
                            {threat.severity.toUpperCase()}
                          </span>
                        </td>
                        <td>{threat.threats[0]?.category}</td>
                        <td className="message-cell">
                          <div className="threat-message">{threat.message}</div>
                        </td>
                        <td>
                          <div className="matched-words">
                            {threat.threats[0]?.matched?.join(", ")}
                          </div>
                        </td>
                        <td className="ip-cell">{threat.ip}</td>
                        <td>
                          {threat.location?.city}, {threat.location?.country}
                        </td>
                        <td>
                          <div className="device-info">
                            {threat.device?.browser} on {threat.device?.os}
                          </div>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="block-ip-btn"
                              onClick={() => blockIP(threat.ip)}
                            >
                              Block IP
                            </button>
                            {threat.severity === "critical" &&
                              !threat.reported && (
                                <button
                                  className="report-btn"
                                  onClick={() => reportToAuthorities(threat)}
                                >
                                  Report
                                </button>
                              )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Blocked IPs */}
            {activeSubTab === "blocked" && (
              <div className="blocked-ips-table">
                <table>
                  <thead>
                    <tr>
                      <th>IP Address</th>
                      <th>Reason</th>
                      <th>Blocked Date</th>
                      <th>Expires</th>
                      <th>Permanent</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {blockedIPs.map((blocked) => (
                      <tr key={blocked._id}>
                        <td className="ip-cell">{blocked.ip}</td>
                        <td>{blocked.reason}</td>
                        <td>
                          {new Date(blocked.blockedAt).toLocaleDateString()}
                        </td>
                        <td>
                          {blocked.permanent
                            ? "Never"
                            : new Date(blocked.expiresAt).toLocaleDateString()}
                        </td>
                        <td>
                          <span
                            className={`permanent-badge ${
                              blocked.permanent ? "yes" : "no"
                            }`}
                          >
                            {blocked.permanent ? "Yes" : "No"}
                          </span>
                        </td>
                        <td>
                          <button
                            className="unblock-btn"
                            onClick={() => unblockIP(blocked._id)}
                          >
                            Unblock
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Lead Details Modal */}
      {selectedLead && (
        <div className="modal-overlay" onClick={() => setSelectedLead(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Lead Details</h2>
            <div className="lead-details">
              <p>
                <strong>Name:</strong> {selectedLead.name}
              </p>
              <p>
                <strong>Email:</strong> {selectedLead.email}
              </p>
              <p>
                <strong>Phone:</strong> {selectedLead.phone}
              </p>
              <p>
                <strong>Service:</strong> {selectedLead.service}
              </p>
              <p>
                <strong>Budget:</strong> {formatCurrency(selectedLead.budget)}
              </p>
              <p>
                <strong>Message:</strong> {selectedLead.message}
              </p>
              <p>
                <strong>Source:</strong> {selectedLead.source}
              </p>
              <p>
                <strong>Submitted:</strong> {formatDate(selectedLead.createdAt)}
              </p>
            </div>
            <div className="modal-actions">
              <button onClick={() => convertLeadToProject(selectedLead._id)}>
                Convert to Project
              </button>
              <button onClick={() => setSelectedLead(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;